import { useContext } from 'react';
import cn from 'classnames';
import {
  ArrowLeft,
  ArrowUUpLeft,
  ArrowUUpRight,
  CircleDashed,
  FloppyDiskBack,
  MagicWand,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  X,
} from '@phosphor-icons/react';
import styles from './Toolbar.module.scss';
import { SVG_SIZE_M } from 'src/constants';
import { AiGenerationStatus, ImageGenRenderData } from 'src/types';
import { Icon } from 'src/components/Icon';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import {
  useImageMaskingPanelActions,
  useImageMaskingBrushSizeToolVisible,
  useCurrentImagesList,
  useSelectedImageIndex,
  useAttachedImageTooltipState,
  useAttachedImageTooltipActions,
  useImageMaskingEditClick,
  useSession,
  useBreakpoint,
} from 'src/hooks';
import { DownloadButton } from 'src/v2/components/DownloadButton';
import { BrushSizeTool } from '../BrushSizeTool';
import { useRightSidePanelActions } from 'src/hooks';
import { Button } from 'src/v2/commonComponents/Button';
import ForwardRefContext from 'src/contexts/ForwardRefContext';

interface ImageEditorToolbarProps {
  brushSize: number;
  setBrushSize: (brushSize: number) => void;
  currentMaskingImageData: ImageGenRenderData | null;
  handleUndo: () => void;
  handleRedo: () => void;
  handleSave: () => void;
  handleDeleteSelections: () => void;
  isRedoButtonDisabled: boolean;
  isUndoButtonDisabled: boolean;
  isEditingMode: boolean;
  zoomScale: number;
}

export const ImageEditorToolbar = ({
  brushSize,
  setBrushSize,
  currentMaskingImageData,
  handleDeleteSelections,
  handleUndo,
  handleRedo,
  handleSave,
  isRedoButtonDisabled,
  isUndoButtonDisabled,
  isEditingMode,
  zoomScale,
}: ImageEditorToolbarProps) => {
  const {
    onChangeImageMaskingRightPanelData,
    resetImageMaskingPanelInfo,
    toggleVisibleBrushSizePanel,
    setEditingMode,
  } = useImageMaskingPanelActions();
  const { isBrushSizeToolVisible } = useImageMaskingBrushSizeToolVisible();
  const { toggleRightSidePanelExpanded } = useRightSidePanelActions();
  const { isAttachedImageEditingMode } = useAttachedImageTooltipState();
  const {
    onToggleImageEditTooltipVisible,
    onChangeIsAttachedImageEditingMode,
  } = useAttachedImageTooltipActions();
  const { selectedImageIndex } = useSelectedImageIndex();
  const { currentImagesList } = useCurrentImagesList();
  const { enableEditingMode } = useImageMaskingEditClick();
  const { isOpenTier } = useSession();
  const { isMobileOrTablet } = useBreakpoint();

  const { onClearAttachments } = useContext(ThreadInputBoxContext);
  const { imageEditorSwiperRef } = useContext(ForwardRefContext);

  const handleEdit = () => {
    toggleVisibleBrushSizePanel(!isBrushSizeToolVisible);
  };

  const handleCloseEditingModal = () => {
    resetImageMaskingPanelInfo();
    onChangeImageMaskingRightPanelData(null);
    toggleRightSidePanelExpanded(false);

    if (!isAttachedImageEditingMode) {
      onClearAttachments();
    } else {
      onToggleImageEditTooltipVisible(true);
      onChangeIsAttachedImageEditingMode(false);
    }
  };

  const handleBackClick = () => {
    setEditingMode(false);
    handleDeleteSelections();

    if (!isAttachedImageEditingMode) {
      onClearAttachments();
    }
  };

  const handleEditButtonClick = () => {
    if (currentMaskingImageData?.url) {
      enableEditingMode({
        currentImageUrl: currentMaskingImageData.url,
      });
    }
  };

  const handleZoomInClick = () => {
    imageEditorSwiperRef.current?.swiper?.zoom?.in();
  };

  const handleZoomOutClick = () => {
    imageEditorSwiperRef.current?.swiper?.zoom?.out();
  };

  const isZoomedIn = zoomScale > 1;
  const isZoomedOut = zoomScale <= 1;

  const withControlButtons =
    !isEditingMode &&
    !currentMaskingImageData?.isDisabled &&
    !currentMaskingImageData?.withWatermark &&
    (selectedImageIndex === null ||
      currentImagesList[selectedImageIndex]?.status ===
        AiGenerationStatus.SUCCESS);

  return (
    <div className={styles.root}>
      {!isEditingMode ? (
        <div className={styles.controls}>
          <div className={styles.modelName}>
            {currentMaskingImageData?.icon && (
              <span className={styles.icon}>
                <Icon size={SVG_SIZE_M} type={currentMaskingImageData.icon} />
              </span>
            )}
            <span>{currentMaskingImageData?.title}</span>
            {withControlButtons && (
              <Button
                color="secondary"
                onClick={handleEditButtonClick}
                disabled={isOpenTier}
              >
                <MagicWand size={SVG_SIZE_M} />
                Edit
              </Button>
            )}
          </div>

          <div className={styles.controlsButtons}>
            {!isMobileOrTablet && (
              <>
                <Button
                  color="secondary"
                  shape="round"
                  onClick={handleZoomInClick}
                  disabled={isZoomedIn}
                >
                  <MagnifyingGlassPlus size={SVG_SIZE_M} />
                </Button>

                <Button
                  color="secondary"
                  shape="round"
                  onClick={handleZoomOutClick}
                  disabled={isZoomedOut}
                >
                  <MagnifyingGlassMinus size={SVG_SIZE_M} />
                </Button>
              </>
            )}

            {currentMaskingImageData?.url && withControlButtons && (
              <DownloadButton url={currentMaskingImageData?.url} />
            )}

            <Button
              color="secondary"
              shape="round"
              onClick={handleCloseEditingModal}
            >
              <X size={SVG_SIZE_M} />
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.controlsButtonsWrapper}>
          <div className={styles.toolbarActions}>
            <Button color="secondary" shape="round" onClick={handleBackClick}>
              <ArrowLeft size={SVG_SIZE_M} />
            </Button>
            <Button
              color="transparent"
              onClick={handleUndo}
              disabled={isUndoButtonDisabled}
            >
              <ArrowUUpLeft size={SVG_SIZE_M} />
            </Button>
            <Button
              color="transparent"
              onClick={handleRedo}
              disabled={isRedoButtonDisabled}
            >
              <ArrowUUpRight size={SVG_SIZE_M} />
            </Button>
            {isMobileOrTablet && (
              <Button
                color="transparent"
                shape="round"
                onClick={handleSave}
                disabled={isUndoButtonDisabled}
              >
                <FloppyDiskBack size={SVG_SIZE_M} />
              </Button>
            )}
            <Button onClick={handleEdit} color="transparent">
              <CircleDashed
                size={SVG_SIZE_M}
                className={cn({
                  [styles.active]: isBrushSizeToolVisible,
                })}
              />
            </Button>
          </div>
          <div className={styles.brushSizeTool}>
            {isBrushSizeToolVisible && (
              <BrushSizeTool
                brushSize={brushSize}
                setBrushSize={setBrushSize}
                isBrushSizeToolVisible={isBrushSizeToolVisible}
                handleDeleteSelections={handleDeleteSelections}
                isRedoButtonDisabled={isRedoButtonDisabled}
                isUndoButtonDisabled={isUndoButtonDisabled}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
